import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const PAGE_SIZE = 10;

const TenantManagement = () => {
  const getInitialStatus = () => {
    try {
      if (typeof window !== 'undefined' && window.location && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        return params.get('status') || '';
      }
    } catch { /* non-browser test environment */ }
    return '';
  };

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(getInitialStatus());
  const [currentPage, setCurrentPage] = useState(1);

  // Tenant Details Modal state
  const [selectedTenantDetails, setSelectedTenantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTab, setDetailsTab] = useState('config'); // 'config' | 'health' | 'audit' | 'schema'
  const [healthHistory, setHealthHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [schemaReport, setSchemaReport] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [rotatingSecret, setRotatingSecret] = useState(false);

  // Health Check Modal state
  const [healthCheckResult, setHealthCheckResult] = useState(null);
  const [checkingHealthId, setCheckingHealthId] = useState(null);

  // Suspend / Reactivate / Offboard action state
  const [actionConfirm, setActionConfirm] = useState(null); // { tenant, action: 'suspend' | 'reactivate' | 'offboard' }
  const [actionReason, setActionReason] = useState('');
  const [retentionDays, setRetentionDays] = useState(90);
  const [modalError, setModalError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const navigate = useNavigate();

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await saasPlatformService.getTenants(search, statusFilter);
      if (response && response.data) {
        setTenants(response.data);
      } else if (Array.isArray(response)) {
        setTenants(response);
      } else {
        setTenants([]);
      }
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to load tenants', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to retrieve tenants from platform catalog.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadTenants();
    }, 250);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  const handleFilterChange = (val) => {
    setStatusFilter(val);
    try {
      if (typeof window !== 'undefined' && window.history && window.location) {
        const url = new URL(window.location.href);
        if (val) {
          url.searchParams.set('status', val);
        } else {
          url.searchParams.delete('status');
        }
        window.history.replaceState({}, '', url.toString());
      }
    } catch { /* safe fallback for mock environments */ }
  };

  const viewDetails = async (tenantId) => {
    setDetailsLoading(true);
    setDetailsTab('config');
    setHealthHistory([]);
    setAuditLogs([]);
    setSchemaReport(null);
    try {
      const res = await saasPlatformService.getTenantDetails(tenantId);
      setSelectedTenantDetails(res.data || res);
    } catch (err) {
      alert('Failed to load tenant details: ' + (err?.response?.data?.message || err?.message));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSwitchDetailsTab = async (tab) => {
    setDetailsTab(tab);
    const tenantObj = selectedTenantDetails?.tenant || selectedTenantDetails;
    const tid = tenantObj?.tenantId || tenantObj?.TenantID || tenantObj?.tenantID || selectedTenantDetails?.tenantId;
    if (!tid) return;

    if (tab === 'health' && healthHistory.length === 0) {
      setTabLoading(true);
      try {
        const res = await saasPlatformService.getTenantHealthHistory(tid, 20);
        setHealthHistory(res.data || res || []);
      } catch (err) {
        console.error('Failed loading tenant health history', err);
      } finally {
        setTabLoading(false);
      }
    } else if (tab === 'audit' && auditLogs.length === 0) {
      setTabLoading(true);
      try {
        const res = await saasPlatformService.getAuditLogs({ tenantId: tid, limit: 20 });
        setAuditLogs(res.data || res || []);
      } catch (err) {
        console.error('Failed loading tenant audit logs', err);
      } finally {
        setTabLoading(false);
      }
    } else if (tab === 'schema' && !schemaReport) {
      setTabLoading(true);
      try {
        const res = await saasPlatformService.getTenantCompatibility(tid);
        setSchemaReport(res.data || res || null);
      } catch (err) {
        console.error('Failed loading tenant schema compatibility report', err);
      } finally {
        setTabLoading(false);
      }
    }
  };

  const handleRotateSecret = async (tenantId) => {
    setRotatingSecret(true);
    setModalError(null);
    try {
      const res = await saasPlatformService.rotateTenantSecret(tenantId, 'v1');
      setSuccessMessage(res?.message || `Tenant credential encryption rotated to key version 'v1'.`);
      await viewDetails(tenantId);
    } catch (err) {
      setModalError(err?.response?.data?.message || err?.message || 'Failed to rotate tenant secret.');
    } finally {
      setRotatingSecret(false);
    }
  };

  const runOnDemandHealthCheck = async (tenant) => {
    setCheckingHealthId(tenant.tenantId);
    setError(null);
    try {
      const res = await saasPlatformService.checkTenantHealth(tenant.tenantId);
      const data = res.data || res;
      setHealthCheckResult({
        tenant,
        data,
        checkedAt: new Date().toLocaleTimeString()
      });
    } catch (err) {
      setHealthCheckResult({
        tenant,
        error: err?.response?.data?.message || err?.message || 'Database health check failed.',
        checkedAt: new Date().toLocaleTimeString()
      });
    } finally {
      setCheckingHealthId(null);
    }
  };

  const openActionConfirm = (tenant, action) => {
    setActionConfirm({ tenant, action });
    setActionReason('');
    setRetentionDays(90);
    setModalError(null);
  };

  const handleActionConfirm = async () => {
    if (!actionConfirm) return;
    if (actionConfirm.action === 'suspend' && actionReason.trim().length < 5) {
      setModalError('A valid suspension reason of at least 5 characters is required.');
      return;
    }
    if (actionConfirm.action === 'offboard' && actionReason.trim().length < 5) {
      setModalError('A valid offboarding reason of at least 5 characters is required.');
      return;
    }

    setActionLoading(true);
    setModalError(null);
    try {
      if (actionConfirm.action === 'suspend') {
        await saasPlatformService.suspendTenant(actionConfirm.tenant.tenantId, actionReason.trim());
        setSuccessMessage(`Tenant '${actionConfirm.tenant.tenantCode}' was successfully suspended.`);
      } else if (actionConfirm.action === 'reactivate') {
        await saasPlatformService.reactivateTenant(actionConfirm.tenant.tenantId);
        setSuccessMessage(`Tenant '${actionConfirm.tenant.tenantCode}' was successfully reactivated.`);
      } else if (actionConfirm.action === 'offboard') {
        await saasPlatformService.offboardTenant(actionConfirm.tenant.tenantId, actionReason.trim(), retentionDays);
        setSuccessMessage(`Tenant '${actionConfirm.tenant.tenantCode}' offboarded under a ${retentionDays}-day retention policy.`);
      }
      setActionConfirm(null);
      await loadTenants();
    } catch (err) {
      setModalError(err?.response?.data?.message || err?.message || `Failed to ${actionConfirm.action} tenant.`);
    } finally {
      setActionLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(tenants.length / PAGE_SIZE));
  const paginatedTenants = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return tenants.slice(start, start + PAGE_SIZE);
  }, [tenants, currentPage]);

  return (
    <div className="saas-container">
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🏢</span> Tenant Fleet Management
          </h1>
          <p>Tenant Catalog, Host Domains, Application Databases &amp; Lifecycle Controls</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-primary" onClick={() => navigate('/saas/onboarding')}>
            ➕ Onboard New Tenant
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div>
            <strong>Error:</strong> {error}
          </div>
          <button className="saas-btn saas-btn-secondary" style={{ padding: '0.3rem 0.6rem' }} onClick={loadTenants}>
            Retry
          </button>
        </div>
      )}

      {successMessage && (
        <div className="saas-success-box">
          <div>
            <strong>Success:</strong> {successMessage}
          </div>
          <button className="saas-btn saas-btn-secondary" style={{ padding: '0.2rem 0.5rem' }} onClick={() => setSuccessMessage(null)}>
            ✕
          </button>
        </div>
      )}

      {/* Filters Bar */}
      <div className="saas-filters">
        <input
          type="text"
          className="saas-search-input"
          placeholder="🔍 Search by Tenant Code or Organization Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="saas-select"
          value={statusFilter}
          onChange={(e) => handleFilterChange(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">ACTIVE Only</option>
          <option value="SUSPENDED">SUSPENDED Only</option>
          <option value="PROVISIONING">PROVISIONING Only</option>
        </select>
        <button className="saas-btn saas-btn-secondary" onClick={loadTenants} disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {/* Tenants Table */}
      <div className="saas-card">
        <div className="saas-card-header">
          <h3>Registered Tenants ({tenants.length})</h3>
          {statusFilter && (
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Filtered by: <strong>{statusFilter}</strong> ({tenants.length} results)
            </span>
          )}
        </div>
        <div className="saas-table-container">
          <table className="saas-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tenant Code</th>
                <th>Organization Name</th>
                <th>Primary Domain</th>
                <th>Database</th>
                <th>Isolation Model</th>
                <th>Status</th>
                <th>Created Date</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                    ⏳ Querying Platform Catalog...
                  </td>
                </tr>
              ) : paginatedTenants.length > 0 ? (
                paginatedTenants.map((t) => (
                  <tr key={t.tenantId}>
                    <td style={{ fontFamily: 'monospace' }}>#{t.tenantId}</td>
                    <td>
                      <strong style={{ color: '#0284c7' }}>{t.tenantCode}</strong>
                    </td>
                    <td>{t.organizationName}</td>
                    <td style={{ fontFamily: 'monospace' }}>{t.primaryDomain}</td>
                    <td style={{ fontFamily: 'monospace', color: '#475569' }}>{t.databaseName}</td>
                    <td>
                      <span className="saas-badge saas-badge-info">{t.isolationModel}</span>
                    </td>
                    <td>
                      <span
                        className={
                          t.status === 'ACTIVE'
                            ? 'saas-badge saas-badge-active'
                            : t.status === 'SUSPENDED'
                            ? 'saas-badge saas-badge-suspended'
                            : 'saas-badge saas-badge-provisioning'
                        }
                      >
                        ● {t.status}
                      </span>
                    </td>
                    <td style={{ color: '#64748b' }}>
                      {new Date(t.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
                        <button
                          className="saas-btn saas-btn-secondary"
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                          onClick={() => viewDetails(t.tenantId)}
                          title="View configuration, health history, and audit trail"
                        >
                          👁️ Details
                        </button>

                        <button
                          className="saas-btn saas-btn-secondary"
                          style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem', borderColor: '#a855f7', color: '#7e22ce' }}
                          onClick={() => navigate('/saas/clinical')}
                          title="Manage clinical investigation catalogue & reference ranges"
                        >
                          🔬 Clinical
                        </button>

                        <button
                          className="saas-btn saas-btn-secondary"
                          style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem', borderColor: '#10b981', color: '#047857' }}
                          onClick={() => navigate('/saas/wallet')}
                          title="Inspect wallet balance & credit transactions"
                        >
                          💰 Wallet
                        </button>

                        <button
                          className="saas-btn saas-btn-secondary"
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', borderColor: '#38bdf8' }}
                          onClick={() => runOnDemandHealthCheck(t)}
                          disabled={checkingHealthId === t.tenantId}
                          title="Run immediate database connectivity & schema health check"
                        >
                          {checkingHealthId === t.tenantId ? '⏳ Checking...' : '🩺 Health'}
                        </button>

                        {t.tenantId !== 1 && (
                          <>
                            {t.status === 'ACTIVE' ? (
                              <button
                                className="saas-btn saas-btn-danger"
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                                onClick={() => openActionConfirm(t, 'suspend')}
                              >
                                🔒 Suspend
                              </button>
                            ) : t.status === 'SUSPENDED' ? (
                              <button
                                className="saas-btn saas-btn-success"
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                                onClick={() => openActionConfirm(t, 'reactivate')}
                              >
                                🔓 Reactivate
                              </button>
                            ) : null}

                            {t.status !== 'OFFBOARDED' && (
                              <button
                                className="saas-btn saas-btn-secondary"
                                style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem', color: '#64748b' }}
                                onClick={() => openActionConfirm(t, 'offboard')}
                                title="Offboard tenant under legal data retention hold"
                              >
                                📦 Offboard
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    No tenants found matching your search and filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {tenants.length > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, tenants.length)} to{' '}
              {Math.min(currentPage * PAGE_SIZE, tenants.length)} of {tenants.length} tenants
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="saas-btn saas-btn-secondary"
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.8125rem' }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                ← Previous
              </button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.8125rem', fontWeight: 600 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="saas-btn saas-btn-secondary"
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.8125rem' }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* On-Demand Health Check Result Modal */}
      {healthCheckResult && (
        <div className="saas-modal-backdrop" onClick={() => setHealthCheckResult(null)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="saas-modal-header">
              <h3>
                🩺 Live Health Check: {healthCheckResult.tenant.tenantCode}
              </h3>
              <button
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }}
                onClick={() => setHealthCheckResult(null)}
              >
                ✕
              </button>
            </div>
            <div className="saas-modal-body">
              {healthCheckResult.error ? (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '1rem', borderRadius: '6px', color: '#b91c1c' }}>
                  <strong>Health Check Alert:</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>{healthCheckResult.error}</p>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                    <div>
                      <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>Tenant Organization</div>
                      <strong>{healthCheckResult.tenant.organizationName}</strong>
                    </div>
                    <div>
                      <span
                        className={
                          healthCheckResult.data?.status === 'HEALTHY'
                            ? 'saas-badge saas-badge-active'
                            : healthCheckResult.data?.status === 'DEGRADED'
                            ? 'saas-badge saas-badge-provisioning'
                            : 'saas-badge saas-badge-suspended'
                        }
                        style={{ fontSize: '0.875rem', padding: '0.35rem 0.75rem' }}
                      >
                        ● {healthCheckResult.data?.status || 'UNKNOWN'}
                      </span>
                    </div>
                  </div>

                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '1rem', fontSize: '0.8125rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <span style={{ color: '#64748b' }}>Response Latency:</span>{' '}
                        <strong>{healthCheckResult.data?.latencyMs != null ? `${healthCheckResult.data.latencyMs} ms` : '—'}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748b' }}>Consecutive Failures:</span>{' '}
                        <strong>{healthCheckResult.data?.consecutiveFailures ?? 0}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748b' }}>Schema Health:</span>{' '}
                        <strong>{healthCheckResult.data?.schemaHealth || 'PASS'}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748b' }}>Check Timestamp:</span>{' '}
                        <span>{healthCheckResult.checkedAt}</span>
                      </div>
                    </div>

                    {healthCheckResult.data?.failureReason && (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0', color: '#b91c1c' }}>
                        <strong>Diagnostic Details:</strong> {healthCheckResult.data.failureReason}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="saas-modal-footer">
              <button className="saas-btn saas-btn-secondary" onClick={() => setHealthCheckResult(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Tab Tenant Details Modal */}
      {selectedTenantDetails && (
        <div className="saas-modal-backdrop" onClick={() => setSelectedTenantDetails(null)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px' }}>
            <div className="saas-modal-header">
              <h3>
                Tenant Details: {selectedTenantDetails.tenant?.tenantCode || selectedTenantDetails.tenant?.TenantCode} — {selectedTenantDetails.tenant?.organizationName || selectedTenantDetails.tenant?.OrganizationName}
              </h3>
              <button
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }}
                onClick={() => setSelectedTenantDetails(null)}
              >
                ✕
              </button>
            </div>

            {/* Modal Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 1.5rem', background: '#f8fafc' }}>
              <button
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: detailsTab === 'config' ? '#0284c7' : '#64748b',
                  borderBottom: detailsTab === 'config' ? '2px solid #0284c7' : '2px solid transparent'
                }}
                onClick={() => handleSwitchDetailsTab('config')}
              >
                ⚙️ Configuration &amp; Database
              </button>
              <button
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: detailsTab === 'health' ? '#0284c7' : '#64748b',
                  borderBottom: detailsTab === 'health' ? '2px solid #0284c7' : '2px solid transparent'
                }}
                onClick={() => handleSwitchDetailsTab('health')}
              >
                🩺 Health History
              </button>
              <button
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: detailsTab === 'audit' ? '#0284c7' : '#64748b',
                  borderBottom: detailsTab === 'audit' ? '2px solid #0284c7' : '2px solid transparent'
                }}
                onClick={() => handleSwitchDetailsTab('audit')}
              >
                📜 Audit Trail
              </button>
              <button
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: detailsTab === 'schema' ? '#0284c7' : '#64748b',
                  borderBottom: detailsTab === 'schema' ? '2px solid #0284c7' : '2px solid transparent'
                }}
                onClick={() => handleSwitchDetailsTab('schema')}
              >
                🧩 Schema &amp; SPs
              </button>
            </div>

            <div className="saas-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {detailsTab === 'config' && (
                <>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#0f172a' }}>Domain Mappings</h4>
                  <div style={{ marginBottom: '1.5rem' }}>
                    {selectedTenantDetails.domains && selectedTenantDetails.domains.length > 0 ? (
                      <table className="saas-table" style={{ fontSize: '0.8125rem' }}>
                        <thead>
                          <tr>
                            <th>Domain Name</th>
                            <th>Primary</th>
                            <th>Verified</th>
                            <th>Registered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTenantDetails.domains.map((d) => (
                            <tr key={d.domainID}>
                              <td style={{ fontFamily: 'monospace' }}>{d.domainName}</td>
                              <td>{d.isPrimary ? '✅ Primary' : 'Secondary'}</td>
                              <td>{d.isVerified ? '✅ Verified' : 'Pending'}</td>
                              <td>{new Date(d.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p style={{ color: '#94a3b8', margin: 0 }}>No custom domains mapped.</p>
                    )}
                  </div>

                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#0f172a' }}>Application Database Metadata</h4>
                  {selectedTenantDetails.database ? (
                    <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '6px', fontSize: '0.8125rem', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <div><strong>Server:</strong> {selectedTenantDetails.database.databaseServer}:{selectedTenantDetails.database.databasePort}</div>
                        <div><strong>Database:</strong> <span style={{ fontFamily: 'monospace' }}>{selectedTenantDetails.database.databaseName}</span></div>
                        <div><strong>User:</strong> {selectedTenantDetails.database.databaseUser}</div>
                        <div><strong>Max Pool Size:</strong> {selectedTenantDetails.database.maxPoolSize}</div>
                        <div><strong>Connection Timeout:</strong> {selectedTenantDetails.database.connectionTimeoutSeconds}s</div>
                        <div><strong>Schema Version:</strong> {selectedTenantDetails.database.schemaVersion || '01_init'}</div>
                      </div>
                      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.75rem' }}>
                          🔒 Password and connection strings are securely encrypted via AES-256-GCM and redacted from API responses.
                        </p>
                        <button
                          className="saas-btn saas-btn-secondary"
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', borderColor: '#38bdf8' }}
                          onClick={() => handleRotateSecret(selectedTenantDetails.tenant.tenantId)}
                          disabled={rotatingSecret}
                          title="Rotate database credential encryption to active key version"
                        >
                          {rotatingSecret ? '⏳ Rotating...' : '🔑 Rotate Encryption Key'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: '#94a3b8', margin: 0 }}>Using Shared Development Default Connection.</p>
                  )}
                </>
              )}

              {detailsTab === 'health' && (
                <div>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#0f172a' }}>Recent Database Health Checks</h4>
                  {tabLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>⏳ Loading health check history...</div>
                  ) : healthHistory.length > 0 ? (
                    <div className="saas-table-container">
                      <table className="saas-table" style={{ fontSize: '0.8125rem' }}>
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th>Latency</th>
                            <th>Consecutive Fails</th>
                            <th>Failure Diagnostic</th>
                            <th>Checked At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {healthHistory.map((h, i) => (
                            <tr key={i}>
                              <td>
                                <span
                                  className={
                                    h.status === 'HEALTHY'
                                      ? 'saas-badge saas-badge-active'
                                      : h.status === 'DEGRADED'
                                      ? 'saas-badge saas-badge-provisioning'
                                      : 'saas-badge saas-badge-suspended'
                                  }
                                >
                                  ● {h.status}
                                </span>
                              </td>
                              <td>{h.latencyMs != null ? `${h.latencyMs} ms` : '—'}</td>
                              <td>{h.consecutiveFailures ?? 0}</td>
                              <td style={{ color: h.failureReason ? '#b91c1c' : '#64748b' }}>{h.failureReason || 'Nominal'}</td>
                              <td style={{ color: '#64748b' }}>{new Date(h.checkedAt).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ color: '#94a3b8', margin: 0 }}>No historical health check records found for this tenant.</p>
                  )}
                </div>
              )}

              {detailsTab === 'audit' && (
                <div>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#0f172a' }}>Tenant Platform Audit Events</h4>
                  {tabLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>⏳ Loading audit log events...</div>
                  ) : auditLogs.length > 0 ? (
                    <div className="saas-table-container">
                      <table className="saas-table" style={{ fontSize: '0.8125rem' }}>
                        <thead>
                          <tr>
                            <th>Action</th>
                            <th>Actor</th>
                            <th>Details</th>
                            <th>Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.map((log) => (
                            <tr key={log.auditID}>
                              <td>
                                <span className="saas-badge saas-badge-info">{log.action}</span>
                              </td>
                              <td><strong>{log.actorUsername}</strong></td>
                              <td style={{ color: '#475569' }}>{log.details || '—'}</td>
                              <td style={{ color: '#64748b' }}>{new Date(log.createdAt).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ color: '#94a3b8', margin: 0 }}>No audit log events recorded for this tenant.</p>
                  )}
                </div>
              )}
              {detailsTab === 'schema' && (
                <div>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#0f172a' }}>Schema Inventory &amp; Stored Procedure Compliance</h4>
                  {tabLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>⏳ Inspecting database schema and stored procedures...</div>
                  ) : schemaReport ? (
                    <div>
                      {(() => {
                        const isCompliant = schemaReport.isCompliant !== undefined
                          ? schemaReport.isCompliant
                          : (schemaReport.compatibilityStatus === 0 || schemaReport.compatibilityStatus === 1 || (schemaReport.missingTables?.length === 0 && (!schemaReport.blockingReasons || schemaReport.blockingReasons.length === 0)));
                        const procCount = schemaReport.proceduresCount ?? (schemaReport.storedProcedureDetails ? Object.keys(schemaReport.storedProcedureDetails).length : (schemaReport.procedures?.length ?? '—'));
                        const tblCount = schemaReport.tablesCount ?? (schemaReport.missingTables ? `Audited (${schemaReport.missingTables.length} missing)` : (schemaReport.tables?.length ?? 'Audited'));
                        const sVer = schemaReport.currentSchemaVersion || schemaReport.schemaVersion || schemaReport.applicationSchemaVersion || '1.0.0-sp87';

                        return (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
                              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>COMPLIANCE</div>
                                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: isCompliant ? '#047857' : '#b91c1c' }}>
                                  {isCompliant ? '✅ COMPLIANT' : '⚠️ DISCREPANT'}
                                </div>
                              </div>
                              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>TABLES COUNT</div>
                                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#0f172a' }}>
                                  {tblCount}
                                </div>
                              </div>
                              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>PROCEDURES</div>
                                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#0f172a' }}>
                                  {procCount}
                                </div>
                              </div>
                              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>SCHEMA VERSION</div>
                                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#0284c7' }}>
                                  {sVer}
                                </div>
                              </div>
                            </div>

                            {schemaReport.blockingReasons && schemaReport.blockingReasons.length > 0 && (
                              <div style={{ marginBottom: '1rem', background: '#fef2f2', border: '1px solid #fecaca', padding: '0.75rem', borderRadius: '6px' }}>
                                <strong style={{ color: '#b91c1c', fontSize: '0.8125rem' }}>⚠️ Compatibility Drift / Blockers:</strong>
                                <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0, fontSize: '0.75rem', color: '#991b1b' }}>
                                  {schemaReport.blockingReasons.map((r, idx) => <li key={idx}>{r}</li>)}
                                </ul>
                              </div>
                            )}

                            {schemaReport.missingProcedures && schemaReport.missingProcedures.length > 0 && (
                              <div style={{ marginBottom: '1rem', background: '#fef2f2', border: '1px solid #fecaca', padding: '0.75rem', borderRadius: '6px' }}>
                                <strong style={{ color: '#b91c1c', fontSize: '0.8125rem' }}>⚠️ Missing Stored Procedures:</strong>
                                <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0, fontSize: '0.75rem', color: '#991b1b' }}>
                                  {schemaReport.missingProcedures.map((p, idx) => <li key={idx}>{p}</li>)}
                                </ul>
                              </div>
                            )}

                            {schemaReport.extraProcedures && schemaReport.extraProcedures.length > 0 && (
                              <div style={{ marginBottom: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '0.75rem', borderRadius: '6px' }}>
                                <strong style={{ color: '#475569', fontSize: '0.8125rem' }}>ℹ️ Additional / Tenant-Specific Stored Procedures:</strong>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                                  {schemaReport.extraProcedures.map((p, idx) => (
                                    <span key={idx} className="saas-badge saas-badge-secondary">{p}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <p style={{ color: '#94a3b8', margin: 0 }}>No schema report available for this tenant.</p>
                  )}
                </div>
              )}
            </div>

            <div className="saas-modal-footer">
              <button className="saas-btn saas-btn-secondary" onClick={() => setSelectedTenantDetails(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {actionConfirm && (
        <div className="saas-modal-backdrop" onClick={() => !actionLoading && setActionConfirm(null)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()}>
            <div className="saas-modal-header">
              <h3>
                {actionConfirm.action === 'suspend'
                  ? '🔒 Confirm Tenant Suspension'
                  : actionConfirm.action === 'offboard'
                  ? '📦 Confirm Legal Tenant Offboarding'
                  : '🔓 Confirm Tenant Reactivation'}
              </h3>
            </div>
            <div className="saas-modal-body">
              <p style={{ margin: '0 0 0.75rem 0' }}>
                Are you sure you want to {actionConfirm.action} tenant{' '}
                <strong>{actionConfirm.tenant.tenantCode}</strong> ({actionConfirm.tenant.organizationName})?
              </p>
              <div style={{ fontSize: '0.8125rem', color: '#475569', background: '#f8fafc', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #e2e8f0' }}>
                <div><strong>Primary Domain:</strong> {actionConfirm.tenant.primaryDomain}</div>
                <div><strong>Current Status:</strong> {actionConfirm.tenant.status}</div>
              </div>

              {actionConfirm.action === 'offboard' ? (
                <>
                  <p style={{ color: '#b91c1c', fontSize: '0.8125rem', background: '#fef2f2', padding: '0.75rem', borderRadius: '4px', margin: '0 0 1rem 0' }}>
                    ⚠️ Offboarding terminates all API domain routing immediately. Tenant data will remain securely retained under legal hold before permanent destruction.
                  </p>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.35rem', color: '#0f172a' }}>
                      Data Retention Hold Period (Days, minimum 30):
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="730"
                      className="saas-search-input"
                      style={{ width: '100%' }}
                      value={retentionDays}
                      onChange={(e) => setRetentionDays(parseInt(e.target.value, 10) || 30)}
                      disabled={actionLoading}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.35rem', color: '#0f172a' }}>
                      Offboarding Reason (Required, min 5 characters):
                    </label>
                    <textarea
                      className="saas-search-input"
                      style={{ width: '100%', minHeight: '70px', resize: 'vertical' }}
                      placeholder="e.g. Contract termination, regulatory closure, or customer request"
                      value={actionReason}
                      onChange={(e) => {
                        setActionReason(e.target.value);
                        setModalError(null);
                      }}
                      disabled={actionLoading}
                    />
                  </div>
                </>
              ) : actionConfirm.action === 'suspend' ? (
                <>
                  <p style={{ color: '#b91c1c', fontSize: '0.8125rem', background: '#fef2f2', padding: '0.75rem', borderRadius: '4px', margin: '0 0 1rem 0' }}>
                    ⚠️ When suspended, all incoming API requests on this tenant's domain will immediately fail-closed with HTTP 403 Forbidden.
                  </p>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.35rem', color: '#0f172a' }}>
                      Suspension Reason (Required, min 5 characters):
                    </label>
                    <textarea
                      className="saas-search-input"
                      style={{ width: '100%', minHeight: '75px', resize: 'vertical' }}
                      placeholder="e.g. Compliance review or customer request"
                      value={actionReason}
                      onChange={(e) => {
                        setActionReason(e.target.value);
                        setModalError(null);
                      }}
                      disabled={actionLoading}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.35rem', color: '#0f172a' }}>
                    Reactivation Note (Optional):
                  </label>
                  <input
                    type="text"
                    className="saas-search-input"
                    style={{ width: '100%' }}
                    placeholder="e.g. Account reinstated after payment"
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    disabled={actionLoading}
                  />
                </div>
              )}

              {modalError && (
                <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginTop: '0.75rem', background: '#fef2f2', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>
                  ⚠️ {modalError}
                </div>
              )}
            </div>
            <div className="saas-modal-footer">
              <button className="saas-btn saas-btn-secondary" onClick={() => setActionConfirm(null)} disabled={actionLoading}>
                Cancel
              </button>
              <button
                className={`saas-btn ${actionConfirm.action === 'suspend' || actionConfirm.action === 'offboard' ? 'saas-btn-danger' : 'saas-btn-success'}`}
                onClick={handleActionConfirm}
                disabled={actionLoading || ((actionConfirm.action === 'suspend' || actionConfirm.action === 'offboard') && actionReason.trim().length < 5)}
              >
                {actionLoading ? 'Processing...' : `Yes, ${actionConfirm.action === 'suspend' ? 'Suspend Tenant' : actionConfirm.action === 'offboard' ? 'Offboard Tenant' : 'Reactivate Tenant'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
